import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { getCategories, getProducts } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { ProductCard } from "@/components/ProductCard";
import { Reveal } from "@/components/motion";
import { FALLBACK_IMG } from "@/lib/images";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronRight, SlidersHorizontal } from "lucide-react";

const PRICE_FILTERS = [
  { id: "all", label: "Tümü", min: null, max: null },
  { id: "u600", label: "600 TL altı", min: null, max: 600 },
  { id: "m", label: "600 – 1.200 TL", min: 600, max: 1200 },
  { id: "o1200", label: "1.200 TL üzeri", min: 1200, max: null },
];

const STYLE_FILTERS = [
  { id: "all", label: "Tüm Tarzlar" },
  { id: "minimal", label: "Minimal" },
  { id: "romantik", label: "Romantik" },
  { id: "isiltili", label: "Işıltılı" },
  { id: "gunluk", label: "Günlük" },
  { id: "davet", label: "Davet" },
  { id: "hediye", label: "Hediye" },
  { id: "trend", label: "Trend" },
];

const OCCASION_FILTERS = [
  { id: "all", label: "Tüm Anlar" },
  { id: "gunluk", label: "Günlük" },
  { id: "ofis", label: "Ofis" },
  { id: "aksam", label: "Akşam" },
  { id: "ozel-gun", label: "Özel Gün" },
  { id: "hediye", label: "Hediye" },
];

export default function Category() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [sort, setSort] = useState("popular");
  const [price, setPrice] = useState("all");
  const [style, setStyle] = useState("all");
  const [occasion, setOccasion] = useState("all");
  const [subcat, setSubcat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [availability, setAvailability] = useState("all");

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setStyle(searchParams.get("style") || "all");
    setOccasion(searchParams.get("occasion") || "all");
    setPrice("all");
    setSubcat(null);
    setAvailability("all");
    const isAll = slug === "tumu";
    Promise.all([getCategories(), getProducts(isAll ? { limit: 60 } : { category: slug, limit: 60 })])
      .then(([cats, prods]) => {
        const matchedCategory = cats.find((item) => item.slug === slug);
        if (!isAll && !matchedCategory) {
          setNotFound(true);
          setCategory(null);
          setProducts([]);
          return;
        }
        setCategory(isAll
          ? { name: "Tüm Ürünler", description: "Her tarza, her anına", image: cats[0]?.image }
          : matchedCategory);
        setProducts(prods);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug, searchParams]);

  const subcats = useMemo(() => {
    const map = new Map();
    products.forEach((p) => {
      if (!p.subcategory) return;
      map.set(p.subcategory, (map.get(p.subcategory) || 0) + 1);
    });
    return Array.from(map.entries()).map(([name, count]) => ({ name, count }));
  }, [products]);

  const filtered = useMemo(() => {
    const f = PRICE_FILTERS.find((p) => p.id === price);
    let list = products.filter((p) =>
      (f.min === null || p.price >= f.min) && (f.max === null || p.price <= f.max) &&
      (style === "all" || p.style === style) &&
      (occasion === "all" || p.occasion === occasion) &&
      (subcat === null || p.subcategory === subcat) &&
      (availability === "all" || !(p.stockStatus === "out_of_stock" || p.stock === 0))
    );
    const sorted = [...list];
    if (sort === "price_asc") sorted.sort((a, b) => a.price - b.price);
    else if (sort === "price_desc") sorted.sort((a, b) => b.price - a.price);
    else if (sort === "newest") sorted.sort((a, b) => b.order - a.order);
    else sorted.sort((a, b) => b.reviews_count - a.reviews_count);
    return sorted;
  }, [products, price, style, occasion, subcat, sort, availability]);

  const categoryUrl = `https://www.shuletaki.com.tr/kategori/${slug}`;
  const jsonLd = category ? [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: category.name,
      description: category.description,
      url: categoryUrl,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Ana Sayfa", item: "https://www.shuletaki.com.tr/" },
        { "@type": "ListItem", position: 2, name: category.name, item: categoryUrl },
      ],
    },
  ] : null;

  if (!loading && notFound) {
    return (
      <>
        <Seo title="Kategori Bulunamadı" description="Aradığınız kategori bulunamadı." path={`/kategori/${slug}`} noindex />
        <div className="container-shule py-24 text-center">
          <h1 className="font-serif text-4xl text-shule-coffee">Kategori bulunamadı.</h1>
          <Link to="/" className="btn-primary-shule mt-8">Ana Sayfaya Dön</Link>
        </div>
      </>
    );
  }

  const chipCls = (active) =>
    `min-h-[44px] px-4 rounded-full border text-sm font-medium transition-colors ${active ? "bg-shule-coffee text-shule-paper border-shule-coffee" : "border-shule-lineaccent hover:border-shule-gold"}`;

  const filters = (
    <div className="space-y-8">
      {subcats.length > 0 && (
        <div>
          <p className="eyebrow mb-3">{category?.name}</p>
          <ul className="space-y-0.5">
            <li>
              <button data-testid="subcat-all" onClick={() => setSubcat(null)}
                className={`w-full flex items-center justify-between py-2.5 min-h-[44px] text-sm border-b border-shule-line/60 transition-colors ${subcat === null ? "font-bold text-shule-goldhover" : "text-shule-mocha hover:text-shule-goldhover"}`}>
                Tüm {category?.name}
                <span className="text-xs text-shule-soft">{products.length}</span>
              </button>
            </li>
            {subcats.map((s) => (
              <li key={s.name}>
                <button data-testid={`subcat-${s.name.toLowerCase().replace(/\s/g, "-")}`} onClick={() => setSubcat(subcat === s.name ? null : s.name)}
                  className={`w-full flex items-center justify-between py-2.5 min-h-[44px] text-sm border-b border-shule-line/60 transition-colors ${subcat === s.name ? "font-bold text-shule-goldhover" : "text-shule-mocha hover:text-shule-goldhover"}`}>
                  {s.name}
                  <span className="text-xs text-shule-soft">{s.count}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div>
        <p className="eyebrow mb-3">Tarzına Göre</p>
        <div className="flex flex-wrap gap-2">
          {STYLE_FILTERS.map((s) => (
            <button key={s.id} data-testid={`style-filter-${s.id}`} onClick={() => setStyle(s.id)} className={chipCls(style === s.id)}>
              {s.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="eyebrow mb-3">Kullanım Anı</p>
        <div className="flex flex-wrap gap-2">
          {OCCASION_FILTERS.map((o) => (
            <button key={o.id} data-testid={`occasion-filter-${o.id}`} onClick={() => setOccasion(o.id)} className={chipCls(occasion === o.id)}>
              {o.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="eyebrow mb-3">Stok Durumu</p>
        <div className="flex flex-wrap gap-2">
          <button data-testid="availability-all" onClick={() => setAvailability("all")} className={chipCls(availability === "all")}>Tümü</button>
          <button data-testid="availability-in-stock" onClick={() => setAvailability("in_stock")} className={chipCls(availability === "in_stock")}>Stoktakiler</button>
        </div>
      </div>
      <div>
        <p className="eyebrow mb-3">Fiyat Aralığı</p>
        <div className="flex flex-wrap gap-2">
          {PRICE_FILTERS.map((f) => (
            <button key={f.id} data-testid={`price-filter-${f.id}`} onClick={() => setPrice(f.id)} className={chipCls(price === f.id)}>
              {f.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Seo title={category?.name} description={category?.description} path={`/kategori/${slug}`} image={category?.image} jsonLd={jsonLd} />
      <section className="container-shule pt-6 lg:pt-8">
        <div data-testid="category-hero" className="relative rounded-[2rem] border border-shule-line bg-shule-paper overflow-hidden min-h-[190px] flex items-center">
          {category?.image && (
            <>
              <img src={category.image} alt={category.name} onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
                className="absolute right-0 top-0 h-full w-1/2 sm:w-2/5 object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-shule-paper via-shule-paper/85 to-transparent" aria-hidden="true" />
            </>
          )}
          <div className="relative p-7 sm:p-10">
            <nav data-testid="breadcrumb" className="flex items-center gap-1.5 text-xs text-shule-soft mb-3" aria-label="Sayfa yolu">
              <Link to="/" className="hover:text-shule-goldhover min-h-[24px] inline-flex items-center">Ana Sayfa</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-shule-mocha">{category?.name}</span>
            </nav>
            <Reveal>
              <h1 data-testid="category-title" className="font-serif text-4xl sm:text-5xl text-shule-coffee">{category?.name}</h1>
              <p className="mt-2 font-serif italic text-shule-goldhover text-lg">Her Tarza, Her Anına</p>
              {category?.description && <p className="mt-1 text-sm text-shule-mocha max-w-md">{category.description}</p>}
            </Reveal>
          </div>
        </div>
      </section>

      <div className="container-shule py-10 lg:py-14 grid lg:grid-cols-[260px_1fr] gap-10">
        <aside className="hidden lg:block">{filters}</aside>

        <div>
          <div className="flex gap-2 overflow-x-auto pb-2 mb-4 lg:hidden scrollbar-hide">
            <button onClick={() => setSort("popular")} className={chipCls(sort === "popular")}>Önerilen</button>
            <button onClick={() => setSort("newest")} className={chipCls(sort === "newest")}>Yeni Gelenler</button>
            <button onClick={() => setAvailability(availability === "in_stock" ? "all" : "in_stock")} className={chipCls(availability === "in_stock")}>Stoktakiler</button>
            <button onClick={() => { setPrice("all"); setStyle("all"); setOccasion("all"); setSubcat(null); setAvailability("all"); }} className={chipCls(false)}>Temizle</button>
          </div>
          <div className="flex items-center justify-between gap-3 mb-6">
            <p data-testid="product-count" className="text-sm text-shule-soft">
              {loading ? "Yükleniyor…" : `${filtered.length} ürün`}
            </p>
            <div className="flex items-center gap-2">
              <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
                <SheetTrigger asChild>
                  <button data-testid="mobile-filter-button" className="lg:hidden min-h-[44px] px-4 rounded-full border border-shule-lineaccent text-sm font-semibold inline-flex items-center gap-2">
                    <SlidersHorizontal className="w-4 h-4" /> Filtrele
                  </button>
                </SheetTrigger>
                <SheetContent side="bottom" className="rounded-t-[2rem] bg-shule-paper border-shule-line px-6 pb-10 max-h-[85vh] overflow-y-auto">
                  <SheetTitle className="font-serif text-2xl font-normal mb-6">Filtrele</SheetTitle>
                  {filters}
                  <button data-testid="mobile-filter-apply" onClick={() => setDrawerOpen(false)} className="btn-primary-shule w-full mt-8">
                    Sonuçları Gör ({filtered.length})
                  </button>
                </SheetContent>
              </Sheet>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger data-testid="sort-select" className="min-h-[44px] w-[180px] rounded-full border-shule-lineaccent bg-shule-paper">
                  <SelectValue placeholder="Sırala" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="popular">Önerilen</SelectItem>
                  <SelectItem value="newest">Yeni Gelenler</SelectItem>
                  <SelectItem value="price_asc">Fiyat (Artan)</SelectItem>
                  <SelectItem value="price_desc">Fiyat (Azalan)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {filtered.length === 0 && !loading ? (
            <p data-testid="no-products-message" className="text-center text-shule-soft py-20 font-serif text-2xl">
              Bu filtrelerle eşleşen ürün bulunamadı.
            </p>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
              {filtered.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
